import { useEffect, useRef, useState } from "react";

export type PresenceState = "entering" | "entered" | "exiting" | "exited";

export type UsePresenceOptions = {
    /** Exit animation duration before unmount (ms). Default 200. */
    exitMs?: number;
    /** Enter animation duration before flipping to entered (ms). Default 300. */
    enterMs?: number;
};

export type UsePresenceResult = {
    mounted: boolean;
    state: PresenceState;
};

const prefersReducedMotion = (): boolean => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Keeps a node mounted through exit so CSS close animations can finish.
 */
export const usePresence = (
    visible: boolean,
    { exitMs = 200, enterMs = 300 }: UsePresenceOptions = {}
): UsePresenceResult => {
    const [mounted, setMounted] = useState(visible);
    const [state, setState] = useState<PresenceState>(visible ? "entering" : "exited");
    const mountedRef = useRef(visible);
    const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = () => {
        if (enterTimerRef.current) {
            clearTimeout(enterTimerRef.current);
            enterTimerRef.current = null;
        }
        if (exitTimerRef.current) {
            clearTimeout(exitTimerRef.current);
            exitTimerRef.current = null;
        }
    };

    useEffect(() => {
        clearTimers();
        const reduced = prefersReducedMotion();
        const effectiveEnterMs = reduced ? 0 : enterMs;
        const effectiveExitMs = reduced ? 0 : exitMs;

        if (visible) {
            mountedRef.current = true;
            setMounted(true);
            setState("entering");
            if (effectiveEnterMs <= 0) {
                setState("entered");
            } else {
                enterTimerRef.current = setTimeout(() => {
                    setState("entered");
                    enterTimerRef.current = null;
                }, effectiveEnterMs);
            }
        } else if (mountedRef.current) {
            setState("exiting");
            if (effectiveExitMs <= 0) {
                mountedRef.current = false;
                setMounted(false);
                setState("exited");
            } else {
                exitTimerRef.current = setTimeout(() => {
                    mountedRef.current = false;
                    setMounted(false);
                    setState("exited");
                    exitTimerRef.current = null;
                }, effectiveExitMs);
            }
        } else {
            setState("exited");
        }

        return clearTimers;
    }, [visible, enterMs, exitMs]);

    return { mounted, state };
};

export default usePresence;
